import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Sparkles, SlidersHorizontal } from "lucide-react";
import styles from "./PeopleSearchResults.module.scss";
import PeopleSearchCard from "./PeopleSearchCard.tsx";
import {
  PeopleSearchProfile,
  PeopleSearchTypeOption,
} from "../../types/peopleSearch.ts";
import PeopleSearchWorkerModal from "./PeopleSearchWorkerModal.tsx";

type PeopleSearchResultsProps = {
  isLoading: boolean;
  type: PeopleSearchTypeOption;
  profiles: PeopleSearchProfile[];
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  searchLabel?: string | null;
  onPageChange?: (page: number) => void;
  onViewProfile?: (profile: PeopleSearchProfile) => void;
  onContactWorker?: (profile: PeopleSearchProfile) => void;
  onClearFilters?: () => void;
  onOpenFilters?: () => void;
};

const PAGE_SIZE = 12;

const PeopleSearchResults = ({
  isLoading,
  type,
  profiles,
  totalCount,
  currentPage,
  pageSize,
  searchLabel,
  onPageChange,
  onViewProfile,
  onContactWorker,
  onClearFilters,
  onOpenFilters,
}: PeopleSearchResultsProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const handleOpenFilters = useCallback(() => {
    if (onOpenFilters) onOpenFilters();
  }, [onOpenFilters]);

  const isRemote = typeof onPageChange === "function";
  const effectivePageSize = pageSize ?? PAGE_SIZE;
  const summaryTotal = typeof totalCount === "number" ? totalCount : profiles.length;
  const totalPages = Math.max(1, Math.ceil(summaryTotal / effectivePageSize));

  const [localPage, setLocalPage] = useState(1);
  const [shouldScrollTop, setShouldScrollTop] = useState(false);
  const [workerSelection, setWorkerSelection] = useState<PeopleSearchProfile | null>(null);
  const [workerModalOpen, setWorkerModalOpen] = useState(false);

  useEffect(() => {
    if (isRemote && typeof currentPage === "number" && currentPage > 0) {
      setLocalPage(currentPage);
    } else if (!isRemote) {
      setLocalPage(1);
    }
  }, [isRemote, currentPage, profiles, type]);

  const effectivePage = isRemote ? currentPage ?? 1 : localPage;

  const handleOpenWorker = useCallback((profile: PeopleSearchProfile) => {
    setWorkerSelection(profile);
    setWorkerModalOpen(true);
  }, []);

  const handleCloseWorker = useCallback(() => {
    setWorkerModalOpen(false);
    setWorkerSelection(null);
  }, []);

  const paginatedProfiles = useMemo(() => {
    if (isRemote) {
      return profiles;
    }
    const start = (effectivePage - 1) * PAGE_SIZE;
    return profiles.slice(start, start + PAGE_SIZE);
  }, [isRemote, profiles, effectivePage]);

  const scrollToTop = useCallback(() => {
    const target = listRef.current ?? containerRef.current;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (!isLoading && shouldScrollTop) {
      scrollToTop();
      setShouldScrollTop(false);
    }
  }, [isLoading, shouldScrollTop, scrollToTop, paginatedProfiles.length]);

  const handlePrevious = () => {
    if (effectivePage <= 1) return;
    if (isRemote && onPageChange) {
      onPageChange(effectivePage - 1);
    } else {
      setLocalPage((prev) => Math.max(1, prev - 1));
    }
    setShouldScrollTop(true);
  };

  const handleNext = () => {
    if (effectivePage >= totalPages) return;
    if (isRemote && onPageChange) {
      onPageChange(effectivePage + 1);
    } else {
      setLocalPage((prev) => Math.min(totalPages, prev + 1));
    }
    setShouldScrollTop(true);
  };

  let content: React.ReactNode;

  if (isLoading) {
    const skeletonCount = type === "WORKER" ? 6 : 4;
    content = (
      <>
        <div className={styles.loaderHeader}>
          <span className={styles.loaderTitle}>Buscando perfiles…</span>
          <span className={styles.loaderSubtitle}>Preparamos recomendaciones personalizadas para vos</span>
        </div>
        <div className={styles.skeletonGrid}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <div key={index} className={styles.skeletonCard}>
              <div className={styles.skeletonHeader}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonHeaderInfo}>
                  <span className={styles.skeletonLine} />
                  <span className={styles.skeletonLineShort} />
                </div>
              </div>
              <div className={styles.skeletonBody}>
                <span className={styles.skeletonChip} />
                <span className={styles.skeletonChip} />
                <span className={styles.skeletonChipLong} />
              </div>
              <div className={styles.skeletonFooter}>
                <span className={styles.skeletonButton} />
                <span className={styles.skeletonButtonGhost} />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  } else if (paginatedProfiles.length === 0) {
    content = (
      <div className={styles.empty}>
        <div className={styles.emptyBadge}>
          <Sparkles size={16} />
          Ajustá los filtros
        </div>
        <h3 className={styles.emptyTitle}>No encontramos perfiles con tu combinación actual.</h3>
        <p className={styles.emptyDescription}>
          Probá modificando especialidades, ubicación o tipo de perfil para ampliar los resultados.
        </p>
        <div className={styles.emptyActions}>
          <button
            type="button"
            onClick={() => onClearFilters?.()}
            className={styles.emptyPrimary}
            disabled={!onClearFilters}
          >
            Limpiar filtros
          </button>
          {onOpenFilters && (
            <button type="button" onClick={handleOpenFilters} className={styles.emptySecondary}>
              <SlidersHorizontal size={16} />
              Ajustar filtros
            </button>
          )}
        </div>
      </div>
    );
  } else {
    content = (
      <>
        {searchLabel && (
          <p className={styles.searchLabel}>Buscando: {searchLabel}</p>
        )}
        <div className={styles.summary}>
          <span className={styles.summaryHighlight}>{summaryTotal}</span>
          <span className={styles.summaryText}>
            {summaryTotal === 1 ? "resultado disponible" : "resultados disponibles"}
          </span>
        </div>
        <div className={styles.list} ref={listRef}>
          {paginatedProfiles.map((profile) => (
            <PeopleSearchCard
              key={`${profile.type}-${profile.id}`}
              profile={profile}
              currentType={type}
              onViewProfile={onViewProfile}
              onContactWorker={onContactWorker}
              onOpenWorkerDetails={handleOpenWorker}
            />
          ))}
        </div>
        {totalPages > 1 && (
          <footer className={styles.pagination}>
            <button type="button" onClick={handlePrevious} disabled={effectivePage === 1}>
              Anterior
            </button>
            <span>
              Página {effectivePage} de {totalPages}
            </span>
            <button type="button" onClick={handleNext} disabled={effectivePage === totalPages}>
              Siguiente
            </button>
          </footer>
        )}
      </>
    );
  }

  return (
    <section className={styles.wrapper} ref={containerRef}>
      {content}
      <PeopleSearchWorkerModal
        isOpen={workerModalOpen}
        worker={workerSelection}
        onClose={handleCloseWorker}
        onContact={onContactWorker}
      />
    </section>
  );
};

export default PeopleSearchResults;
